import React from 'react';
import { useTranslation } from 'react-i18next';
import { LayoutDashboard } from 'lucide-react';
import Navbar from './Navbar';
import AdminTabs from './AdminTabs';
import { Skeleton } from './ui/skeleton';

/**
 * Pantalla de carga personalizada para la sección de Administración.
 *
 * Equivalente al ProviderLoadingScreen pero con el ícono del panel de control,
 * para que el admin reconozca su consola mientras cargan los datos.
 *
 * Props:
 *  - message:  texto bajo el loader (por defecto, mensaje de admin).
 *  - chrome:   si true (defecto) incluye Navbar + contenedor; si false,
 *              renderiza solo el bloque del loader (para áreas de contenido
 *              que ya tienen el chrome del admin montado).
 *  - skeleton: si true, dibuja un esqueleto de las tarjetas KPI del dashboard.
 *  - className: clases extra para el bloque del loader (p. ej. padding).
 */
export default function AdminLoadingScreen({
  message,
  chrome = true,
  skeleton = false,
  className = '',
}) {
  const { t } = useTranslation();
  const text = message ?? t('admin.loadingPanel', 'Cargando el panel de administración...');

  const loader = (
    <div
      role="status"
      aria-live="polite"
      aria-busy="true"
      className={`flex flex-col items-center justify-center ${className}`}
    >
      {/* Spinner doble anillo de marca con el ícono del panel en el centro */}
      <div className="relative w-20 h-20">
        <div className="absolute inset-0 rounded-full border-4 border-[#36C0C7]/20" />
        <div className="absolute inset-0 rounded-full border-4 border-transparent border-t-[#36C0C7] border-r-[#134168] animate-spin" />
        <div className="absolute inset-0 flex items-center justify-center">
          <LayoutDashboard className="w-7 h-7 text-[#134168]" />
        </div>
      </div>
      <p className="mt-5 text-sm font-semibold tracking-wide text-[#134168]">{text}</p>
      <span className="sr-only">{t('common.loading')}</span>
    </div>
  );

  if (!chrome) return loader;

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <AdminTabs />
      <div className="container mx-auto px-4 md:px-6 max-w-7xl py-8">
        {skeleton && (
          <>
            <Skeleton className="h-10 w-56 md:w-64 mb-6 md:mb-8" />
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4 mb-8">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="bg-white rounded-2xl p-4 md:p-6 shadow-sm">
                  <Skeleton className="w-10 h-10 md:w-12 md:h-12 rounded-xl mb-2 md:mb-3" />
                  <Skeleton className="h-7 w-20 mb-2" />
                  <Skeleton className="h-4 w-24" />
                </div>
              ))}
            </div>
          </>
        )}
        <div className="py-12 md:py-16">{loader}</div>
      </div>
    </div>
  );
}
